/**
 * Donation Form Handler
 * ======================================
 * Handles donation form submission and integration with Giver Care API
 * - Validates required fields
 * - Handles custom amount input
 * - Submits to API endpoint
 * - Displays success/error messages
 * - Handles card payment redirects
 */

const FORM_SELECTOR = '.donation-form__form';

class DonationForm {
	constructor() {
		this.form = document.querySelector(FORM_SELECTOR);

		if (!this.form) {
			return;
		}

		// Read API endpoint from HTML data attribute (easily editable in Twig)
		this.apiEndpoint = this.form.dataset.apiUrl || '/api/donation.php';

		this.init();
	}

	init() {
		// Handle custom amount toggle
		this.setupCustomAmountToggle();

		// Handle price group switching (monthly vs single)
		this.setupPriceGroupToggle();

		// Require phone for monthly card payments
		this.setupPhoneRequirement();

		// Handle form submission
		this.form.addEventListener('submit', this.handleSubmit);

		// Handle return from payment gateway
		this.handlePaymentReturn();
	}

	setupCustomAmountToggle() {
		const priceRadios = this.form.querySelectorAll('input[name="price"]');
		const customAmountContainer = this.form.querySelector('.donation-form__item--price');

		if (!customAmountContainer) {
			return;
		}

		// Hide custom amount input by default
		customAmountContainer.style.display = 'none';

		priceRadios.forEach(radio => {
			radio.addEventListener('change', (e) => {
				if (e.target.value === 'custom') {
					customAmountContainer.style.display = 'block';
					const input = customAmountContainer.querySelector('input');
					if (input) {
						input.setAttribute('required', 'required');
					}
				} else {
					customAmountContainer.style.display = 'none';
					const input = customAmountContainer.querySelector('input');
					if (input) {
						input.removeAttribute('required');
						input.value = '';
					}
				}
			});
		});
	}

	setupPriceGroupToggle() {
		const timeRadios = this.form.querySelectorAll('input[name="time"]');
		const monthlyGroup = this.form.querySelector('.donation-form__price--monthly');
		const singleGroup = this.form.querySelector('.donation-form__price--single');

		if (!monthlyGroup || !singleGroup) return;

		const switchGroup = (isMonthly) => {
			monthlyGroup.style.display = isMonthly ? '' : 'none';
			singleGroup.style.display = isMonthly ? 'none' : '';

			// Uncheck all price radios and hide custom amount
			this.form.querySelectorAll('input[name="price"]').forEach(r => r.checked = false);
			const customAmountContainer = this.form.querySelector('.donation-form__item--price');
			if (customAmountContainer) {
				customAmountContainer.style.display = 'none';
				const input = customAmountContainer.querySelector('input');
				if (input) { input.removeAttribute('required'); input.value = ''; }
			}

			// Check first radio in active group
			const activeGroup = isMonthly ? monthlyGroup : singleGroup;
			const firstRadio = activeGroup.querySelector('input[name="price"]');
			if (firstRadio) firstRadio.checked = true;
		};

		timeRadios.forEach(radio => {
			radio.addEventListener('change', (e) => switchGroup(e.target.value === 'monthly'));
		});
	}

	setupPhoneRequirement() {
		const phoneInput = this.form.querySelector('input[name="phone"]');
		const phoneLabel = this.form.querySelector('label[for="donation-form-phone"]');

		if (!phoneInput || !phoneLabel) return;

		const update = () => {
			const timeInput = this.form.querySelector('input[name="time"]:checked');
			const typeInput = this.form.querySelector('input[name="type"]:checked');
			const isMonthlyCard = timeInput?.value === 'monthly' && typeInput?.value === 'card';

			phoneInput.required = isMonthlyCard;
			phoneLabel.textContent = isMonthlyCard ? 'Telefon *' : 'Telefon';
		};

		this.form.querySelectorAll('input[name="time"], input[name="type"]').forEach(radio => {
			radio.addEventListener('change', update);
		});

		update();
	}

	handleSubmit = async (e) => {
		e.preventDefault();

		// Get form data
		const formData = this.getFormData();

		if (!formData) {
			this.showError('Prosím vyplňte všechna povinná pole.');
			return;
		}

		// Show loading state
		const submitButton = this.form.querySelector('button[type="submit"]');
		const originalText = submitButton.textContent;
		submitButton.disabled = true;
		submitButton.textContent = 'Odesílám...';

		try {
			// Send to API
			const response = await fetch(this.apiEndpoint, {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json',
				},
				body: JSON.stringify(formData),
			});

			const result = await response.json();

			if (response.ok && result.success) {
				this.handleSuccess(result.data, formData.method);
        if (typeof fbq === 'function') fbq('track', 'donate');
			} else {
				this.handleError(result);
			}
		} catch (error) {
			console.error('Donation form error:', error);
			this.showError('Něco se pokazilo. Zkuste to prosím znovu.');
		} finally {
			submitButton.disabled = false;
			submitButton.textContent = originalText;
		}
	}

	getFormData() {
		// Get periodicity (monthly/single)
		const periodicityInput = this.form.querySelector('input[name="time"]:checked');
		if (!periodicityInput) return null;
		const periodicity = periodicityInput.value; // monthly or single

		// Get amount
		let amount = null;
		const priceRadio = this.form.querySelector('input[name="price"]:checked');
		if (!priceRadio) return null;

		if (priceRadio.value === 'custom') {
			// Get custom amount
			const customInput = this.form.querySelector('input[name="custom_amount"]');
			if (!customInput || !customInput.value) return null;
			amount = parseFloat(customInput.value);
		} else {
			amount = parseFloat(priceRadio.value);
		}

		if (isNaN(amount) || amount <= 0) {
			return null;
		}

		// Get payment method - API codebook: 0=Online (card), 1=Převod (transfer)
		const methodInput = this.form.querySelector('input[name="type"]:checked');
		if (!methodInput) return null;
		const method = methodInput.value === 'card' ? 0 : 1;

		// Get personal info
		const firstnameInput = this.form.querySelector('input[name="firstname"]');
		const surnameInput = this.form.querySelector('input[name="surname"]');
		const emailInput = this.form.querySelector('input[name="email"]');
		const phoneInput = this.form.querySelector('input[name="phone"]');

		if (!firstnameInput?.value || !surnameInput?.value || !emailInput?.value) {
			return null;
		}

		// Get newsletter checkbox
		const newsletterCheckbox = this.form.querySelector('.checkbox input[type="checkbox"]');
		const newsletter = newsletterCheckbox ? newsletterCheckbox.checked : false;

		const payload = {
			firstname: firstnameInput.value.trim(),
			surname: surnameInput.value.trim(),
			email: emailInput.value.trim(),
			amount: amount,
			periodicity: periodicity,
			method: method,
			newsletter: newsletter,
		};

		// Only add phone if it has value
		const phoneValue = phoneInput?.value.trim();
		if (phoneValue) {
			payload.phone = phoneValue;
		}

		// Project ID from block settings (backend falls back to default)
		const projectId = parseInt(this.form.dataset.projectId, 10);
		if (projectId > 0) {
			payload.project = projectId;
		}

		return payload;
	}

	handleSuccess(data, method) {
		// Hide form
		const formWrapper = this.form.querySelector('.donation-form__wrapper');
		if (formWrapper) {
			formWrapper.style.display = 'none';
		}

		if (method === 0) {
			// Card payment - redirect to payment gateway
			if (data.onlinePaymentDetails && data.onlinePaymentDetails.url) {
				const redirecting = this.form.querySelector('.donation-form__redirecting');
				if (redirecting) redirecting.style.display = 'block';
				window.location.href = data.onlinePaymentDetails.url;
			} else {
				this.showError('Chyba při přesměrování na platební bránu.');
			}
		} else {
			// Transfer payment - show QR and payment details
			this.showTransferDetails(data);
		}
	}

	showTransferDetails(data) {
		const successContainer = this.form.querySelector('.donation-form__success');

		if (!successContainer) {
			alert('Děkujeme za Váš dar! Platební údaje Vám byly zaslány e-mailem.');
			return;
		}

		const qrImg = successContainer.querySelector('.donation-form__qr-image');
		const qrBlock = successContainer.querySelector('.donation-form__qr');
		const vsElement = successContainer.querySelector('.donation-form__vs');
		const accountElement = successContainer.querySelector('.donation-form__account');
		const amountElement = successContainer.querySelector('.donation-form__amount');
		const detailsBlock = successContainer.querySelector('.donation-form__payment-details');
		const emailNote = successContainer.querySelector('.donation-form__note');
		const standingOrder = successContainer.querySelector('.donation-form__standing-order');

		const details = data.trasnsferPaymentDetails;

		// Always show VS and amount if available
		if (vsElement && details?.identification) {
			vsElement.textContent = details.identification;
		}
		if (amountElement && data.amount) {
			amountElement.textContent = parseFloat(data.amount).toLocaleString('cs-CZ') + ' Kč';
		}
		if (detailsBlock) detailsBlock.style.display = 'flex';

		// Číslo účtu – zobrazit vždy když ho máme (api.php doplňuje i pro měsíční)
		if (details?.bankAccount && accountElement) {
			accountElement.closest('.donation-form__payment-item').style.display = 'block';
			accountElement.textContent = details.bankAccount;
		} else if (accountElement) {
			accountElement.closest('.donation-form__payment-item').style.display = 'none';
		}

		// QR kód – jen pokud ho API vrátí (jednorázové platby)
		if (details?.qr && qrImg && qrBlock) {
			qrImg.src = details.qr;
			qrBlock.style.display = 'flex';
			if (standingOrder) standingOrder.style.display = 'none';
			if (emailNote) emailNote.style.display = 'none';
		} else {
			if (qrBlock) qrBlock.style.display = 'none';
			if (standingOrder) standingOrder.style.display = 'block';
			if (emailNote) emailNote.style.display = 'block';
		}

		successContainer.style.display = 'block';
		successContainer.scrollIntoView({ behavior: 'smooth', block: 'start' });
	}

	handleError(result) {
		console.error('API error:', result);
		this.showError(result.error || 'Došlo k chybě při zpracování platby.');
	}

	showError(message) {
		const errorContainer = this.form.querySelector('.donation-form__error');

		if (errorContainer) {
			const errorMessage = errorContainer.querySelector('.donation-form__error-message');
			if (errorMessage) {
				errorMessage.textContent = message;
			}
			errorContainer.style.display = 'block';
			errorContainer.scrollIntoView({ behavior: 'smooth', block: 'start' });
		} else {
			// Fallback to alert
			alert(message);
		}
	}

	handlePaymentReturn() {
		// Check if we're returning from payment gateway
		const urlParams = new URLSearchParams(window.location.search);
		const donation = urlParams.get('donation');

		if (!donation) {
			return;
		}

		// Hide form
		const formWrapper = this.form.querySelector('.donation-form__wrapper');
		if (formWrapper) {
			formWrapper.style.display = 'none';
		}

		if (donation === 'success') {
			const successContainer = this.form.querySelector('.donation-form__success-card');
			if (successContainer) {
				successContainer.style.display = 'block';
				successContainer.scrollIntoView({ behavior: 'smooth', block: 'start' });
			} else {
				this.showError('Platba byla úspěšně provedena. Děkujeme!');
			}

      if (typeof fbq === 'function') fbq('track', 'donate-card-success');
		} else if (donation === 'error') {
			this.showError('Platba nebyla dokončena. Zkuste to prosím znovu nebo zvolte jiný způsob platby.');
		}
	}
}

export default DonationForm;
